import { createApp } from "vue";
import i18n from "@plugins/i18n";
import DOMUtils from "@utils/dom.utils";
import stpUtils from "@plugins/stp/stp.utils";
import StpTooltip from "@plugins/stp/components/StpTooltip";
import StpTooltipModal from "@plugins/stp/components/StpTooltipModal";
import richTextUtils from "@plugins/richtext/utils/richtext.utils";
import AppInstanceUtils from "@plugins/common/service/app.instance.utils";
import stpEventBus from "@plugins/richtext/plugins/stp/service/event-bus";
export default function (editor) {
  const STP_SELECTOR = stpUtils.SELECTOR.STP;
  const STP_TAG_SELECTOR = stpUtils.SELECTOR.STP_TAG;
  const STP_COMPONENT_SELECTOR = stpUtils.SELECTOR.STP_COMPONENT;
  const STP_COMPONENT_WRAPPER_SELECTOR = stpUtils.SELECTOR.STP_COMPONENT_WRAPPER;

  const richTextEditor = editor.options.get("richTextEditor");
  let modalId = null;

  (function init() {
    // register the rich text common functions for STP.
    richTextEditor.plugins["stp"] = {
      renderInEditor,
      cleanUp,
      containPluginElements,
    };
  })();

  /**
   * Function to convert existing stp tag to StpTooltip component.
   */
  function renderInEditor() {
    DOMUtils.elOf(editor.getBody(), STP_TAG_SELECTOR).forEach((element) => {
      // Retrieve data to create the STP tooltip component
      const stpData = stpUtils.retrieveDataFromStpTooltip(element);
      const StpTooltipApp = createApp(StpTooltip, {
        ...AppInstanceUtils.generateDynamicMountProps(richTextEditor),
        stpData: stpData,
        showTooltip: false,
      });
      AppInstanceUtils.installCommonPlugin(StpTooltipApp);

      // Wrap a non-editable layer
      let wrapper = DOMUtils.createElement("span", {
        class: "sls-stp-wrapper",
        contenteditable: false,
      });
      element.parentNode.insertBefore(wrapper, element);
      wrapper.appendChild(element);

      // Replace created STP tooltip component with the element
      const richTextStpToolTip = StpTooltipApp.mount(element);
      DOMUtils.replaceComponentWrapperWithComponentElement(richTextStpToolTip);
      // Handle case when plugin component is last item,
      // add a paragraph to allow user to continue adding content.
      const stpWrapper = richTextStpToolTip.$el.closest(STP_COMPONENT_WRAPPER_SELECTOR);
      richTextUtils.addNewLineIfIsLastElement(stpWrapper);
    });
  }

  /**
   * Function to clean up the rich text content.
   * Convert StpTooltip component to STP tag for saving.
   *
   * @param content
   * @returns {*}
   */
  function cleanUp(content) {
    let container = document.createElement("div");
    container.innerHTML = content;
    let clone = container.cloneNode(true);

    // Find StpTooltip component, extract data to construct tag, replace tag with wrapper
    DOMUtils.elOf(clone, STP_COMPONENT_SELECTOR).forEach((element) => {
      let data = stpUtils.retrieveDataFromStpTooltip(element);
      let stpTag = stpUtils.createStpTag(data);
      let stpWrapper = element.closest(STP_COMPONENT_WRAPPER_SELECTOR);
      if (stpWrapper) {
        stpWrapper.replaceWith(stpTag);
      }
    });

    return clone.innerHTML;
  }

  /**
   * Function to determine if the rich text editor contains STP tooltip element(s).
   * @param container
   * @returns {boolean}
   */
  function containPluginElements(container) {
    return container.querySelectorAll(STP_SELECTOR).length > 0;
  }

  /**
   * Register the STP plugin button to the rich text editor.
   */
  editor.ui.registry.addToggleButton("stp", {
    tooltip: i18n.t("tinymce_plugin_stp_tooltip_icon_button_tooltip"),
    icon: "stp",
    onAction: handleClickAction,
    onSetup: handleStpElementSelected,
  });

  /**
   * Function to handle STP button clicked.
   */
  function handleClickAction() {
    // get selected STP (if exist)
    const targetNode = stpUtils.retrieveFirstSelectedStpFromEditor(editor);

    if (notNull(targetNode)) {
      // selected STP existing, edit
      const stpData = stpUtils.retrieveDataFromStpTooltip(targetNode);
      openModal(stpData);
    } else {
      // no selected stp, create

      // For iOS and ipadOS, using the on-screen word suggestion will result in
      // the caret position to not be updated.
      editor.save();

      openModal();
    }
  }

  /**
   * Function to make the STP toolbar tool-icon active when an STP element has been
   * selected in the editor's content.
   * Reference: https://www.tiny.cloud/docs/ui-components/typesoftoolbarbuttons/#togglebutton
   *
   * @param buttonApi
   * @returns {function(): *}
   */
  function handleStpElementSelected(buttonApi) {
    const nodeChangeHandler = function (eventApi) {
      buttonApi.setActive(
        // to determine if the selection has STP element.
        eventApi.parents.some((parent) => {
          return editor.dom.hasClass(parent, "sls-stp-wrapper");
        }),
      );
    };

    // onSetup should always return the unbind handlers.
    editor.on("NodeChange", nodeChangeHandler);
    return function () {
      return editor.off("NodeChange", nodeChangeHandler);
    };
  }

  /**
   * Function to open the STP settings modal to create or update STP tooltip.
   */
  function openModal(selectedStp) {
    // event bus handler between editor and modal
    let eventBusWatchInstances = [];
    eventBusWatchInstances.push(stpEventBus.$on("addStp", handleAddStp));

    // show the modal
    modalId = richTextEditor.$modal.show(
      StpTooltipModal,
      {
        eventBusWatchInstances: eventBusWatchInstances,
        selectedStp: selectedStp,
      },
      {},
      true,
    );
  }

  /**
   * Function to handle the insert/update of STP into the editor.
   *
   * @param stpData
   */
  function handleAddStp(stpData) {
    // close the modal
    richTextEditor.$modal.remove(modalId);

    // create a placeholder to be mounted
    let stpTag = stpUtils.createStpTag(stpData);
    //editor.insertContent("stp placeholder");
    editor.insertContent(stpTag.outerHTML);

    // render STP into the editor
    renderInEditor();
  }
}
